// The IDE's code editor: a CodeMirror 6 view with Munos highlighting and a
// live linter driven by the real WASM compiler.
//
// A deployment is several members, each its own `.munos` document. The editor
// keeps one EditorState per member (so each tab has its own undo history and
// cursor) and swaps the active state into the single view on tab switch. The
// linter compiles the active document for BOTH roles (client + server) — the
// server enforces things the client build doesn't (e.g. uninitialized scalar
// globals), so a script that only ever ran as a client can still be wrong on
// the server half. Errors surface inline (gutter markers) and, via the
// reporter, in the IDE's console panel.

import { EditorState, Compartment } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { linter, type Diagnostic } from "@codemirror/lint";
import { indentWithTab } from "@codemirror/commands";
import { CompileError, type MunosCompiler, type CompileTarget } from "@multinostalgia/munos";
import { munos } from "./munos-lang.js";

export interface DiagnosticSummary {
  label: string;
  errorCount: number;
  // Human-readable lines for the console panel (already role-prefixed).
  messages: string[];
}

export interface EditorDeps {
  parent: HTMLElement;
  compiler: MunosCompiler;
  // Emitted when the active document changes (debounced by CodeMirror's own
  // update cycle). The host persists on this.
  onChange: (label: string, source: string) => void;
  // Emitted after each lint pass for the active member, so the console panel
  // and the Play button can reflect the current error state.
  onDiagnostics: (summary: DiagnosticSummary) => void;
}

// CodeMirror line/column (1-based) → a document position, clamped so a stale
// or out-of-range location from the compiler can never throw.
function locToPos(view: EditorView, line: number, column: number): number {
  const doc = view.state.doc;
  const ln = Math.max(1, Math.min(line, doc.lines));
  const l = doc.line(ln);
  const col = Math.max(1, column);
  return Math.min(l.from + col - 1, l.to);
}

// Pull every `line:column` location the compiler put in a thrown CompileError.
// Two formats (packages/munos-rs/src/compile.rs): a syntax error ends with
// "... at line L:C", and a type-error block leads each entry with "L:C: msg".
function extractLocations(message: string): { line: number; column: number }[] {
  const out: { line: number; column: number }[] = [];
  const syntax = /\bat line (\d+):(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = syntax.exec(message)) !== null) {
    out.push({ line: Number(m[1]), column: Number(m[2]) });
  }
  const typeHead = /^(\d+):(\d+):/gm;
  while ((m = typeHead.exec(message)) !== null) {
    out.push({ line: Number(m[1]), column: Number(m[2]) });
  }
  return out;
}

export class MunosEditor {
  private readonly view: EditorView;
  private readonly deps: EditorDeps;
  private readonly states = new Map<string, EditorState>();
  private activeLabel = "";
  // Swapped per active member so the linter closure always sees the right label.
  private readonly lintCompartment = new Compartment();

  constructor(deps: EditorDeps) {
    this.deps = deps;
    this.view = new EditorView({
      parent: deps.parent,
      state: EditorState.create({ extensions: this.baseExtensions() }),
    });
  }

  private baseExtensions() {
    return [
      basicSetup,
      keymap.of([indentWithTab]),
      munos(),
      EditorView.theme(
        {
          "&": { height: "100%", fontSize: "13px" },
          ".cm-scroller": { fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace" },
        },
        { dark: true },
      ),
      EditorView.updateListener.of((u) => {
        if (u.docChanged && this.activeLabel) {
          this.deps.onChange(this.activeLabel, u.state.doc.toString());
        }
      }),
      this.lintCompartment.of(this.makeLinter()),
    ];
  }

  // A linter bound to `activeLabel`: it compiles the active document for both
  // roles, turns failures into inline diagnostics, and reports a summary.
  private makeLinter() {
    const compiler = this.deps.compiler;
    return linter((view): Diagnostic[] => {
      const source = view.state.doc.toString();
      const diagnostics: Diagnostic[] = [];
      const messages: string[] = [];
      const seen = new Set<string>();

      const push = (line: number, column: number, sev: "error" | "warning", text: string) => {
        const key = `${line}:${column}:${text}`;
        if (seen.has(key)) return;
        seen.add(key);
        const from = locToPos(view, line, column);
        const lineEnd = view.state.doc.lineAt(from).to;
        // A short marker from the column to (at most) the line end; collapses
        // to a point diagnostic on an empty line.
        diagnostics.push({ from, to: Math.min(from + 1, lineEnd), severity: sev, message: text });
        messages.push(text);
      };

      for (const target of ["client", "server"] as CompileTarget[]) {
        try {
          const res = compiler.compile(source, target);
          for (const d of res.diagnostics) {
            push(d.line, d.column, "warning", `[${target}] ${d.message}`);
          }
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          if (e instanceof CompileError || (e instanceof Error && e.name === "CompileError")) {
            const locs = extractLocations(msg);
            const head = msg.split("\n\n")[0] ?? msg;
            if (locs.length === 0) {
              push(1, 1, "error", `[${target}] ${head}`);
            } else {
              for (const loc of locs) push(loc.line, loc.column, "error", `[${target}] ${head}`);
            }
          } else {
            // A non-compile failure (bug in the harness) — surface it plainly.
            push(1, 1, "error", `[${target}] ${msg}`);
          }
        }
      }

      const errorCount = diagnostics.filter((d) => d.severity === "error").length;
      this.deps.onDiagnostics({ label: this.activeLabel, errorCount, messages });
      return diagnostics;
    }, { delay: 500 });
  }

  /** Replace the whole member set. Preserves states for labels that persist. */
  setMembers(members: { label: string; source: string }[], active?: string): void {
    const keep = new Set(members.map((m) => m.label));
    for (const label of [...this.states.keys()]) {
      if (!keep.has(label)) this.states.delete(label);
    }
    for (const m of members) {
      if (!this.states.has(m.label)) {
        this.states.set(m.label, EditorState.create({ doc: m.source, extensions: this.baseExtensions() }));
      }
    }
    const target = active && keep.has(active) ? active : members[0]?.label ?? "";
    if (target) this.showMember(target);
  }

  /** Make `label` the active document in the view. */
  showMember(label: string): void {
    const state = this.states.get(label);
    if (!state) return;
    this.activeLabel = label;
    this.view.setState(state);
  }

  /** Rename a member's state key without losing its document/history. */
  renameMember(oldLabel: string, newLabel: string): void {
    const state = this.states.get(oldLabel);
    if (!state) return;
    this.states.delete(oldLabel);
    this.states.set(newLabel, state);
    if (this.activeLabel === oldLabel) this.activeLabel = newLabel;
  }

  /** Current text of a member (the active view's live text if it's active). */
  sourceOf(label: string): string {
    if (label === this.activeLabel) return this.view.state.doc.toString();
    return this.states.get(label)?.doc.toString() ?? "";
  }

  get active(): string {
    return this.activeLabel;
  }

  focus(): void {
    this.view.focus();
  }
}
